import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import type { VendorUpdateSuggestion } from '../../mocks/data/vendor-update-suggestions'
import { SUPPLY_PERSONAS, useSupplyPersonaStore } from '../../store/role'
import { apiUrl, USE_REAL_API } from '../../lib/api'
import { fetchMe } from '../../lib/auth'
import { formatDate } from '../../lib/datetime'
import { useT } from '../../i18n'
import { SUPPLY_VENDOR_UPDATES_STRINGS } from '../../i18n/supply/vendor-updates'
import { DataTableShell } from '../../components/ui/data-table-shell'

type Response = {
  data: VendorUpdateSuggestion[]
  meta: { page: number; per_page: number; total: number; total_pages: number }
}

const STATUS_BADGE: Record<VendorUpdateSuggestion['status'], string> = {
  pending: 'bg-amber-100 text-amber-800',
  approved: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-rose-100 text-rose-800',
  propagated: 'bg-sky-100 text-sky-800',
}

export function MySubmittedUpdates() {
  const persona = useSupplyPersonaStore((s) => s.supplyPersona)
  const info = SUPPLY_PERSONAS[persona]
  const t = useT(SUPPLY_VENDOR_UPDATES_STRINGS).mySubmittedUpdates

  const realMe = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: fetchMe,
    enabled: USE_REAL_API,
    staleTime: 5 * 60 * 1000,
  })
  const effectiveOrgType = USE_REAL_API
    ? (realMe.data?.active_membership?.org_type ?? 'SO3')
    : info.org_type
  const displayName = USE_REAL_API
    ? (realMe.data?.active_membership?.persona_label ??
        realMe.data?.user.display_name ??
        info.org_name)
    : info.org_name

  const { data, isLoading, isError } = useQuery<Response>({
    queryKey: ['supply', 'my-vendor-update-suggestions', persona],
    queryFn: async () => {
      const res = await fetch(
        apiUrl(`/supply/my-vendor-update-suggestions?persona=${persona}`),
      )
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.json()
    },
    enabled: effectiveOrgType === 'SO3',
  })

  if (effectiveOrgType !== 'SO3') {
    return (
      <div className="max-w-2xl rounded-lg border border-dashed border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-6 text-sm text-[var(--color-text-muted)]">
        {t.nonSo3Gate.prefix}
        <span className="font-medium">{t.nonSo3Gate.so3Strong}</span>{' '}
        {t.nonSo3Gate.personaActiveLabel} {displayName} ({effectiveOrgType}).
        {!USE_REAL_API && <> {t.nonSo3Gate.hint}</>}
      </div>
    )
  }

  return (
    <div className="max-w-5xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--color-text)]">{t.pageTitle}</h1>
          <p className="mt-2 text-[var(--color-text-muted)]">{t.description}</p>
        </div>
        <Link
          to="/supply/my-updates/new"
          className="rounded-md bg-[var(--color-action)] px-4 py-2 text-sm font-medium text-[var(--color-action-foreground)] hover:bg-[var(--color-action-hover)]"
        >
          {t.actions.newUpdate}
        </Link>
      </div>

      <div className="mt-6 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] overflow-hidden">
        {isLoading && (
          <div className="px-4 py-6 text-sm text-[var(--color-text-muted)]">{t.states.loading}</div>
        )}
        {isError && (
          <div className="px-4 py-6 text-sm text-[var(--color-danger)]" role="alert">{t.states.error}</div>
        )}
        {data && data.data.length === 0 && (
          <div className="px-4 py-10 text-center text-sm text-[var(--color-text-muted)]">
            {t.table.emptyState.prefix}{' '}
            <Link to="/supply/my-updates/new" className="text-[var(--color-info)] underline">
              {t.table.emptyState.cta}
            </Link>
            {t.table.emptyState.suffix}
          </div>
        )}
        {data && data.data.length > 0 && (
          <DataTableShell variant="embedded">
          <table>
            <thead>
              <tr>
                <th>{t.table.columnHeaders.product}</th>
                <th>
                  {t.table.columnHeaders.proposedChange}
                </th>
                <th>{t.table.columnHeaders.status}</th>
                <th>
                  {t.table.columnHeaders.submittedAt}
                </th>
                <th>
                  {t.table.columnHeaders.adminNotes}
                </th>
              </tr>
            </thead>
            <tbody>
              {data.data.map((item) => (
                <tr key={item.id}>
                  <td className="py-3 font-medium text-[var(--color-text)]">
                    {item.product_name_snapshot}
                    <div className="text-xs font-normal text-[var(--color-text-muted)]">{item.product_id}</div>
                  </td>
                  <td className="py-3 text-[var(--color-text)]">
                    {item.field_changes.map((fc) => (
                      <div key={fc.field_name} className="text-xs">
                        <span className="font-mono">{fc.field_name}</span>{' '}
                        <span className="text-[var(--color-text-muted)]">{String(fc.old_value)}</span>{' '}
                        <span className="text-[var(--color-text-muted)]">→</span>{' '}
                        <span className="text-[var(--color-text)]">{String(fc.new_value)}</span>
                      </div>
                    ))}
                  </td>
                  <td className="py-3">
                    <span
                      className={
                        'inline-flex rounded-full px-2 py-0.5 text-xs font-medium ' +
                        STATUS_BADGE[item.status]
                      }
                    >
                      {t.statusBadges[item.status]}
                    </span>
                  </td>
                  <td className="py-3 text-xs text-[var(--color-text-muted)]">
                    {formatDate(item.submitted_at)}
                  </td>
                  <td className="py-3 text-xs text-[var(--color-text-muted)]">
                    {(item.admin_notes ?? []).length === 0 ? (
                      <span className="text-[var(--color-text-muted)]">—</span>
                    ) : (
                      <span>
                        {t.adminNotesFormat((item.admin_notes ?? []).length)}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </DataTableShell>
        )}
      </div>
    </div>
  )
}
